// src/app/services/orders.service.ts
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';

import { BaseCacheService } from '../base-cache.service';
import { ResponseDto } from '../../dto/shared.dto';
import {
  OrderByIdResultDto,
  OrdersResultDto,
  SignEndOfDayRequest,
  SignExpeditorNoteResultDto
} from '../../dto/order.dto';
import { Status } from '../../dto/status.enum';
import { SyncAction } from '../../dto/sync-actions.enum';

import { AuthService } from '../auth-service.service';
import { OfflineModeService } from '../offline-mode.service';
import { ErrorToastService } from '../error-toast.service';
import { OfflineWarningModalService } from '../modal/offline-warning-modal.service';
import { BackgroundSyncService } from '../sync/background-sync.service';
import { OrderApiService } from './order-api.service';
import { OrdersStoreEntry } from '../../db-description';

@Injectable({ providedIn: 'root' })
export class OrdersService extends BaseCacheService {
  constructor(
    dbService: NgxIndexedDBService,
    offlineService: OfflineModeService,
    authService: AuthService,
    modalCtrl: ModalController,
    private offlineWarningModalService: OfflineWarningModalService,
    private router: Router,
    private backgroundSyncService: BackgroundSyncService,
    private orderApiService: OrderApiService,
    private errorToastService: ErrorToastService
  ) {
    super(dbService, offlineService, authService, modalCtrl);
  }

  public getOrdersList(
    limit: number,
    offset: number,
    loadingIds: string,
    status?: Status[],
    trackId?: string
  ): Observable<OrdersResultDto> {
    const params = { limit, offset, loadingIds, status };
    const dbName = this.getCurOrdersDbName(status);
    const pageId = params.offset || 0;
    const emptyResult: OrdersResultDto = {
      success: true,
      data: { requests: [], total: 0, currentPageSize: 0 }
    };

    return this.orderApiService
      .getOrdersList(limit, offset, loadingIds, status, trackId)
      .pipe(
        mergeMap(response => {
          if (response.success) {
            this.saveOrdersList(response, params, status);
            this.backgroundSyncService.runAllActions();
            return of(response);
          } else {
            Sentry.captureMessage('getOrdersList returned success=false', {
              level: 'warning',
              extra: { params, response }
            });
            return this.fallbackToCache(dbName, pageId, emptyResult);
          }
        }),
        catchError(error => {
          Sentry.captureException(error);
          return this.fallbackToCache(dbName, pageId, emptyResult);
        })
      );
  }

  public getOrderById(
    forwardingRequestId: number
  ): Observable<OrderByIdResultDto | null> {
    return this.orderApiService.getOrderById(forwardingRequestId);
  }

  public acceptOrder(
    requestId: number
  ): Observable<ResponseDto> {
    const data = { requestId };
    return this.orderApiService.acceptOrder(data).pipe(
      map(response => {
        if (!response?.success) {
          void this.errorToastService.acceptOrderErrorModal();
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          void this.offlineWarningModalService.showOfflineWarningModal();
        } else if (error.status === 424) {
          this.handleNoBankingDataError();
        } else if (error.status === 412) {
          void this.errorToastService.alreadyAcceptedErrorModal();
        } else if (error.status === 409) {
          void this.errorToastService.alreadyAcceptedErrorModal();
        } else {
          void this.errorToastService.acceptOrderErrorModal();
          Sentry.captureException(error);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Отказ от принятой заявки. Возможен, пока агент не подписал поручение экспедитору; после подписи сервер
   * отвечает 409 — экспедитор закреплён за сделкой. Причину показываем: без неё свайп «Отказаться» молча
   * возвращался бы на место, и экспедитор не понял бы, что отказ не состоялся.
   */
  public declineOrder(requestId: number): Observable<ResponseDto> {
    return this.orderApiService.declineOrder({ requestId }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 0) {
          void this.offlineWarningModalService.showOfflineWarningModal();
        } else if (error.status === 409) {
          void this.errorToastService.showError('Поручение уже подписано агентом — отказаться от заявки нельзя');
        } else {
          void this.errorToastService.showError('Не удалось отказаться от заявки. Попробуйте позже');
          Sentry.captureException(error);
        }
        return throwError(() => error);
      })
    );
  }

  public finishForwarding(requestId: number): Observable<{ success: boolean }> {
    return this.orderApiService.finishForwarding(requestId).pipe(
      catchError((error: HttpErrorResponse) => {
        // Повтор уже завершённой заявки сервер отвечает 208 (ALREADY_REPORTED) со строковым телом:
        // разбор JSON у обычного finishForwarding падает и уводит успешную по сути операцию в ошибку.
        // Считаем 208 успехом — заявка завершена, ставить в очередь и показывать ошибку не нужно.
        if (error?.status === 208) {
          return of({ success: true });
        }
        this.backgroundSyncService.addSyncAction(
          requestId,
          SyncAction.endOrder
        );
        return throwError(
          () => new Error('Не удалось завершить заказ, попробуйте позже.')
        );
      })
    );
  }

  public signExpeditorNoteWithBES(
    payload: SignEndOfDayRequest,
    bargainId: number
  ): Observable<SignExpeditorNoteResultDto> {
    return this.orderApiService.signExpeditorNote(payload, bargainId);
  }

  public handleErrorCodeForToastService(code: number) {
    this.errorToastService.sendError(code);
  }

  private saveOrdersList(
    response: OrdersResultDto,
    params: {
      limit: number;
      offset: number;
      loadingIds: string;
      status?: Status[];
    },
    status?: Status[]
  ): void {
    const pageId = params.offset || 0;
    const db = this.getCurOrdersDbName(status);

    if (pageId === 0) {
      this.dbService
        .clear(db)
        .subscribe(() => this.addOneResult(db, response, pageId));
    } else {
      this.addOneResult(db, response, pageId);
    }
  }

  private addOneResult(
    dbName: string,
    response: OrdersResultDto,
    pageId: number
  ) {
    this.dbService
      .add(dbName, {
        pageId,
        user: this.getCurEmail(),
        filter: '',
        response
      })
      .subscribe();
  }

  private fallbackToCache(
    dbName: string,
    pageId: number,
    emptyResult: OrdersResultDto
  ): Observable<OrdersResultDto> {
    return this.dbService
      .getByKey<OrdersStoreEntry>(dbName, [pageId, '', this.getCurEmail()])
      .pipe(
        map((rec?: OrdersStoreEntry) => rec?.response ?? emptyResult),
        catchError(dbErr => {
          Sentry.captureException(dbErr);
          return of(emptyResult);
        })
      );
  }

  private handleNoBankingDataError() {
    this.router.navigate(['banking-data']).catch();
    this.handleErrorCodeForToastService(424);
  }
}
